import { VQA_STATUS_ORDER } from "./assessmentStatuses";

export const REQUEST_PENDING_STATUS = "Request Pending";
export const SCHEDULED_MULTI_SPONSOR_STATUS = "Scheduled Multi-Sponsor";
export const MY_ASSESSMENT_STATUS = "My Assessment";
export const PENDING_DILIGENT_REVIEW = "Pending Diligent Review";

const STATUS_ALIASES = {
  Scheduled: "Assessment Scheduled",
};

const DAY_COLOR_PRIORITY = [
  PENDING_DILIGENT_REVIEW,
  REQUEST_PENDING_STATUS,
  SCHEDULED_MULTI_SPONSOR_STATUS,
];

const STATUS_DOT = {
  [PENDING_DILIGENT_REVIEW]: { dot: "bg-blue-600", bg: "bg-blue-100 text-blue-900" },
  [REQUEST_PENDING_STATUS]: { dot: "bg-yellow-500", bg: "bg-yellow-100 text-yellow-900" },
  [SCHEDULED_MULTI_SPONSOR_STATUS]: { dot: "bg-violet-600", bg: "bg-violet-100 text-violet-900" },
  [MY_ASSESSMENT_STATUS]: { dot: "bg-green-600", bg: "bg-green-100 text-green-900" },
  Scheduled: { dot: "bg-indigo-600", bg: "bg-indigo-100 text-indigo-900" },
  "Assessment Scheduled": { dot: "bg-indigo-600", bg: "bg-indigo-100 text-indigo-900" },
  "Assessment Complete": { dot: "bg-orange-500", bg: "bg-orange-100 text-orange-900" },
  "Final Report Delivered": { dot: "bg-green-600", bg: "bg-green-100 text-green-900" },
  "Report Published to Library": { dot: "bg-green-700", bg: "bg-green-100 text-green-900" },
  "Published to Library": { dot: "bg-green-700", bg: "bg-green-100 text-green-900" },
  Cancelled: { dot: "bg-red-600", bg: "bg-red-100 text-red-900" },
};

export const CALENDAR_SUBHEADING = {
  admin: "All scheduled assessments.",
  sponsor:
    "Scheduled assessments you requested, joined, or available to join as a multi-sponsor participant.",
};

export function calendarSubheading(isAdmin) {
  return isAdmin ? CALENDAR_SUBHEADING.admin : CALENDAR_SUBHEADING.sponsor;
}

export function parseScheduledDate(value) {
  if (!value) return null;
  const datePart = String(value).slice(0, 10);
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(datePart);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }
  return date;
}

export function startOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function statusIndex(status) {
  if (!status) return -1;
  const canonical = STATUS_ALIASES[status] || status;
  return VQA_STATUS_ORDER.indexOf(canonical);
}

export function isAtOrBeyondCalendarStatus(currentStatus, targetStatus) {
  const current = statusIndex(currentStatus);
  const target = statusIndex(targetStatus);
  if (current < 0 || target < 0) return false;
  return current >= target;
}

export function getCalendarDisplayStatus(row, today = new Date()) {
  const status = row?.vqa_status;
  if (!status || status === "Cancelled") return status || null;
  if (row.is_pending_join) return status || PENDING_DILIGENT_REVIEW;

  const schedule = parseScheduledDate(row.assessment_start_date);
  if (!schedule) return status;

  const todayDate = startOfDay(today);
  if (schedule > todayDate && isAtOrBeyondCalendarStatus(status, "Assessment in Progress")) {
    return "Scheduled";
  }
  if (schedule <= todayDate && isAtOrBeyondCalendarStatus(status, "Final Report Delivered")) {
    return status;
  }
  if (schedule < todayDate && !isAtOrBeyondCalendarStatus(status, "Assessment Complete")) {
    return "Assessment Complete";
  }
  return status;
}

export function getSponsorPrimaryBadge(row) {
  if (!row) return null;
  if (row.is_pending_join) return REQUEST_PENDING_STATUS;
  if (row.is_browse_only) return SCHEDULED_MULTI_SPONSOR_STATUS;
  if (row.is_originator || row.is_accepted_participant) return MY_ASSESSMENT_STATUS;
  return null;
}

export function getRowDisplayStatus(row, { isAdmin, today } = {}) {
  if (!isAdmin) {
    const badge = getSponsorPrimaryBadge(row);
    if (badge === REQUEST_PENDING_STATUS || badge === SCHEDULED_MULTI_SPONSOR_STATUS) {
      return badge;
    }
  }
  if (!isAdmin && row?.is_pending_join) return PENDING_DILIGENT_REVIEW;
  return getCalendarDisplayStatus(row, today);
}

export function getDayPrimaryStatus(rows, { isAdmin, today } = {}) {
  if (!rows?.length) return null;
  const statuses = new Set(rows.map((row) => getRowDisplayStatus(row, { isAdmin, today })).filter(Boolean));
  const priority = DAY_COLOR_PRIORITY.find((status) => statuses.has(status));
  if (priority) return priority;

  const earliest = [...rows].sort(compareCalendarRows)[0];
  return getRowDisplayStatus(earliest, { isAdmin, today });
}

export function getStatusColors(status) {
  return STATUS_DOT[status] || { dot: "bg-gray-400", bg: "bg-gray-100 text-gray-900" };
}

export function uniqueStatusDots(rows, { isAdmin, today, limit = 3 } = {}) {
  const seen = [];
  rows.forEach((row) => {
    const status = getRowDisplayStatus(row, { isAdmin, today });
    if (status && !seen.includes(status)) seen.push(status);
  });
  return seen.slice(0, limit);
}

export function compareCalendarRows(a, b) {
  const aTime = parseScheduledDate(a?.assessment_start_date)?.getTime() ?? 0;
  const bTime = parseScheduledDate(b?.assessment_start_date)?.getTime() ?? 0;
  if (aTime !== bTime) return aTime - bTime;
  const aJoin = a?.join_request_created_on ? Date.parse(a.join_request_created_on) : 0;
  const bJoin = b?.join_request_created_on ? Date.parse(b.join_request_created_on) : 0;
  if (aJoin !== bJoin) return aJoin - bJoin;
  return String(a?.sponsor_name || "").localeCompare(String(b?.sponsor_name || ""));
}

const REQUEST_TYPE_RANK = {
  "Initial Request": 0,
  "Joined Request": 1,
  "Request Pending": 2,
};

const CANCEL_BLOCKED_STATUSES = new Set([
  "Cancelled",
  "Canceled Assessment",
  "Archived",
  "Draft Report Delivered",
  "In Sponsor Review",
  "Draft Review by Client",
  "Final Report Delivered",
  "Pending Observation Response",
  "Observation Response In Review",
  "Report Published to Library",
  "Published to Library",
  "Report Anonymized",
  "Assessment Closed",
]);

const JOIN_CLOSED_STATUSES = new Set([
  "Report Published to Library",
  "Published to Library",
  "Report Anonymized",
  "Assessment Closed",
  "Cancelled",
  "Canceled Assessment",
  "Archived",
]);

export function canJoinCalendarAssessment(row, today = new Date()) {
  if (!row?.is_browse_only) return false;
  const status = row?.vqa_status;
  if (status && JOIN_CLOSED_STATUSES.has(status)) return false;
  const scheduled = parseScheduledDate(row?.assessment_start_date);
  if (!scheduled) return false;
  return startOfDay(scheduled) >= startOfDay(today);
}

export function dedupeCalendarBrowseRows(rows) {
  const seen = new Set();
  return (rows || []).filter((row) => {
    if (!row?.is_browse_only) return true;
    const key = row.vqa_request_id || row.vqa_id;
    if (!key) return true;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function canCancelCalendarAssessment(status) {
  if (!status) return false;
  if (CANCEL_BLOCKED_STATUSES.has(status)) return false;
  const draftIdx = VQA_STATUS_ORDER.indexOf("Draft Report Delivered");
  const idx = VQA_STATUS_ORDER.indexOf(status);
  if (idx >= 0 && draftIdx >= 0 && idx >= draftIdx) return false;
  return true;
}

export function groupCalendarRows(rows) {
  const byAssessment = new Map();
  (rows || []).forEach((row) => {
    const key = row.vqa_id || row.vqa_request_id;
    if (!byAssessment.has(key)) byAssessment.set(key, []);
    byAssessment.get(key).push(row);
  });

  return [...byAssessment.values()].map((group) => {
    const sorted = [...group].sort((a, b) => {
      const rankA = REQUEST_TYPE_RANK[a.request_type] ?? (a.is_originator ? 0 : 3);
      const rankB = REQUEST_TYPE_RANK[b.request_type] ?? (b.is_originator ? 0 : 3);
      if (rankA !== rankB) return rankA - rankB;
      return compareCalendarRows(a, b);
    });
    const parent = sorted.find((row) => row.is_originator) || sorted[0];
    const subrows = sorted.filter((row) => row !== parent);
    return { parent, subrows };
  });
}

export function filterCalendarRows(rows, { requestTypes = [], day = null } = {}) {
  return (rows || []).filter((row) => {
    if (!parseScheduledDate(row.assessment_start_date)) return false;
    if (requestTypes.length > 0 && !requestTypes.includes(row.request_type)) return false;
    if (day) {
      const scheduled = parseScheduledDate(row.assessment_start_date);
      if (!scheduled) return false;
      if (
        scheduled.getFullYear() !== day.getFullYear() ||
        scheduled.getMonth() !== day.getMonth() ||
        scheduled.getDate() !== day.getDate()
      ) {
        return false;
      }
    }
    return true;
  });
}

export function rowsOnDay(rows, day) {
  return filterCalendarRows(rows, { day });
}

export function getDayNumberCircleClasses(status, { selected, isToday, hasAssessments } = {}) {
  const base =
    "mx-auto flex h-8 w-8 items-center justify-center rounded-full outline-none focus:outline-none";
  if (!hasAssessments) {
    if (isToday && selected) return `${base} bg-indigo-600 text-white font-semibold`;
    if (isToday) return `${base} text-indigo-600 font-semibold`;
    if (selected) return `${base} bg-indigo-600 text-white font-semibold`;
    return base;
  }
  if (!status) {
    if (selected) return `${base} bg-gray-300 text-gray-900 font-semibold`;
    return `${base} bg-gray-100 font-semibold text-gray-900`;
  }
  const { dot, bg } = getStatusColors(status);
  if (selected) return `${base} ${dot} text-white font-semibold`;
  return `${base} ${bg} font-semibold text-gray-900`;
}

export function rowsInViewSpan(rows, monthDate, spanMonths = 1) {
  const start = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
  const endExclusive = new Date(monthDate.getFullYear(), monthDate.getMonth() + spanMonths, 1);
  return (rows || []).filter((row) => {
    const scheduled = parseScheduledDate(row.assessment_start_date);
    if (!scheduled) return false;
    return scheduled >= start && scheduled < endExclusive;
  });
}

export function buildMonthDays(monthDate) {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const first = new Date(year, month, 1);
  const startOffset = first.getDay();
  const gridStart = new Date(year, month, 1 - startOffset);
  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(gridStart);
    date.setDate(gridStart.getDate() + index);
    return {
      date,
      inMonth: date.getMonth() === month,
      key: `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`,
    };
  });
}

export function sameDay(a, b) {
  if (!a || !b) return false;
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function addedScopeLabels(row) {
  const services = (row?.added_scope || []).map((item) => item?.rfi_name).filter(Boolean);
  const tags = []
    .concat(splitTags(row?.addon_scope_tags), splitTags(row?.sponsor_additional_scope_tags))
    .filter(Boolean);
  return [...services, ...tags];
}

function splitTags(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.filter(Boolean);
  return String(raw)
    .split(/[,;]/)
    .map((tag) => tag.trim())
    .filter(Boolean);
}
